import { createSwitchlessReducer } from '../util/switchlessReducer';
import { Dispatch } from 'redux';
import fulfill from '../fulfill';
import { CORE_API_HOSTNAME } from '../env';
import { InsightCheckoutKpis } from '../types/insight';
import addMonths from 'date-fns/addMonths';
import startOfHour from 'date-fns/startOfHour';
import namespace  from './namespace';
import { createAccountIdChangedCaseReducer } from '../auth/useAccountId';

type KpiData = {
    dailyCapturedKpis: InsightCheckoutKpis[];
    dailyOpenKpis: InsightCheckoutKpis[];
    dailyOpenKpisCreatedAtGte: Date;
}

export type InsightKpiState = {
    data: KpiData;
    loading: boolean;
}

export const insightKpiInitialState: InsightKpiState = {
    data: {
        dailyCapturedKpis: [],
        dailyOpenKpis: [],
        dailyOpenKpisCreatedAtGte: new Date(),
    },
    loading: false,
};

export const { reducer, actions } = createSwitchlessReducer({
    namespace: namespace,
    initialState: insightKpiInitialState,
    reducers: {
        loadInsightKpiDataAttempt: (state: InsightKpiState) => {
            return {
                ...state,
                dailyCapturedKpis: [],
                dailyOpenKpis: [],
                loading: true,
            };
        },
        loadInsightKpiDataSuccess: (
            state: InsightKpiState,
            data: KpiData
        ) => {
            return {
                ...state,
                data: data,
                loading: false,
            };
        },
    },
    globalReducer: createAccountIdChangedCaseReducer(() => insightKpiInitialState),
});

export const createInsightKpiActions = (dispatch: Dispatch, account_id: string) => {
    const loadInsightKpis = async () => {
        dispatch(actions.loadInsightKpiDataAttempt());
        const result = await fetchInsightKpis(account_id);
        dispatch(actions.loadInsightKpiDataSuccess(result));
    };
    return {
        loadInsightKpis,
    };
};

const fetchInsightKpis = async (
    account_id: string
): Promise<KpiData> => {
    const dailyCapturedParams = new URLSearchParams({
        interval: 'day',
        transaction_status: 'CAPTURED',
        aggregate_functions: JSON.stringify(['sum', 'count', 'currency']),
        field: 'total_amount',
        event_created_at_gte: startOfHour(addMonths(new Date(), -6)).toISOString(),
    });
    const dailyCapturedKpisPromise = fulfill.get({
        accountId: account_id,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${account_id}/insight/kpi/checkout?${dailyCapturedParams}`,
        handlers: {
            200: (kpis: InsightCheckoutKpis[]) => (kpis),
            400: () => ([]),
            403: () => ([]),
            404: () => ([]),
            500: () => ([]),
            502: () => ([]),
        },
        cacheBuster: false,
    });

    const dailyOpenKpisCreatedAtGte = startOfHour(addMonths(new Date(), -12));
    const dailyOpenParams = new URLSearchParams({
        interval: 'day',
        transaction_status: 'AUTHORIZED',
        aggregate_functions: JSON.stringify(['sum', 'count', 'currency']),
        field: 'total_amount',
        event_created_at_gte: dailyOpenKpisCreatedAtGte.toISOString(),
    });
    const dailyOpenKpisPromise = fulfill.get({
        accountId: account_id,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${account_id}/insight/kpi/checkout?${dailyOpenParams}`,
        handlers: {
            200: (kpis: InsightCheckoutKpis[]) => (kpis),
            400: () => ([]),
            403: () => ([]),
            404: () => ([]),
            500: () => ([]),
            502: () => ([]),
        },
        cacheBuster: false,
    });

    const [dailyCapturedKpis, dailyOpenKpis] = await Promise.all([dailyCapturedKpisPromise, dailyOpenKpisPromise]);
    return {
        dailyCapturedKpis,
        dailyOpenKpis,
        dailyOpenKpisCreatedAtGte,
    };
};
